import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Grid,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getPartById, updatePart, deletePart } from '../../services/partService';
import { getInventory, adjustInventory, getInventoryTransactions } from '../../services/inventoryService';
import { getLocations } from '../../services/locationService';
import { toast } from 'react-toastify';
import WarehouseReorderList from '../../components/WarehouseReorderList';
import PartTechnicianRequirementList from '../../components/PartTechnicianRequirementList';

const PartDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [part, setPart] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [technicianRequirement, setTechnicianRequirement] = useState(null);

  // State for inventory adjustment modal
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustment, setAdjustment] = useState({
    location_id: '',
    transaction_type: 'receive',
    quantity_change: '',
    notes: ''
  });
  const [adjustmentError, setAdjustmentError] = useState(null);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);

  // State for edit part modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedPart, setEditedPart] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch part details
        const partData = await getPartById(id);
        setPart(partData);

        // Check if part has a technician requirement
        if (partData.technician_requirement) {
          setTechnicianRequirement(partData.technician_requirement);
        }

        // Initialize edited part state with current part data
        setEditedPart({
          internal_part_number: partData.internal_part_number,
          description: partData.description,
          part_type_id: partData.part_type_id,
          is_special_order: partData.is_special_order,
          is_returnable: partData.is_returnable,
          can_bundle: partData.can_bundle,
          cost: partData.cost,
          reorder_point: partData.reorder_point,
          preferred_supplier_id: partData.preferred_supplier_id
        });

        // Fetch inventory for this part
        const inventoryResponse = await getInventory({ part_id: id });
        setInventory(inventoryResponse.items || inventoryResponse); // Handle both new and old response format

        // Fetch transaction history for this part
        const transactionsData = await getInventoryTransactions({ part_id: id });
        setTransactions(transactionsData);

        // Fetch locations for dropdown
        const locationsData = await getLocations();
        setLocations(locationsData);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching part data:', err);
        setError('Failed to load part data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAdjustmentChange = (e) => {
    const { name, value } = e.target;
    setAdjustment({
      ...adjustment,
      [name]: value
    });
  };

  const handleAdjustInventory = async (e) => {
    e.preventDefault();

    // Validate form
    if (!adjustment.location_id || !adjustment.transaction_type || !adjustment.quantity_change) {
      setAdjustmentError('Please fill in all required fields');
      return;
    }

    try {
      setAdjustmentLoading(true);
      setAdjustmentError(null);

      // Prepare data for API
      const adjustmentData = {
        part_id: id,
        location_id: adjustment.location_id,
        transaction_type: adjustment.transaction_type,
        quantity_change: parseInt(adjustment.quantity_change),
        notes: adjustment.notes || undefined
      };

      // Call API to adjust inventory
      await adjustInventory(adjustmentData);

      // Refresh inventory and transactions data
      const inventoryResponse = await getInventory({ part_id: id });
      setInventory(inventoryResponse.items || inventoryResponse); // Handle both new and old response format

      const transactionsData = await getInventoryTransactions({ part_id: id });
      setTransactions(transactionsData);

      // Close modal and reset form
      setShowAdjustModal(false);
      setAdjustment({
        location_id: '',
        transaction_type: 'receive',
        quantity_change: '',
        notes: ''
      });

      toast.success('Inventory adjusted successfully');
      setAdjustmentLoading(false);
    } catch (err) {
      console.error('Error adjusting inventory:', err);
      setAdjustmentError(err.message || 'Failed to adjust inventory');
      setAdjustmentLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedPart({
      ...editedPart,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleUpdatePart = async (e) => {
    e.preventDefault();

    try {
      setEditLoading(true);
      setEditError(null);

      // Call API to update part
      const updatedPart = await updatePart(id, editedPart);

      // Update local state
      setPart(updatedPart);

      // Close modal
      setShowEditModal(false);

      toast.success('Part updated successfully');
      setEditLoading(false);
    } catch (err) {
      console.error('Error updating part:', err);
      setEditError(err.message || 'Failed to update part');
      setEditLoading(false);
    }
  };

  const handleDeletePart = async () => {
    try {
      setDeleteLoading(true);

      // Call API to delete part
      await deletePart(id);

      toast.success('Part deleted successfully');

      // Redirect to inventory list
      navigate('/inventory');
    } catch (err) {
      console.error('Error deleting part:', err);
      toast.error(err.message || 'Failed to delete part');
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Calculate total quantity across all locations
  const totalQuantity = inventory.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Part Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={RouterLink}
              to="/inventory"
              variant="outlined"
              color="inherit"
            >
              Back to Inventory
            </Button>
            <Button
              onClick={() => setShowAdjustModal(true)}
              variant="contained"
              color="primary"
            >
              Adjust Inventory
            </Button>
            <Button
              onClick={() => setShowEditModal(true)}
              variant="contained"
              color="warning"
            >
              Edit Part
            </Button>
            <Button
              onClick={() => setShowDeleteModal(true)}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      ) : part ? (
        <>
          {/* Part Details Paper */}
          <Paper sx={{ mb: 4, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Part Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ '& > p': { mb: 2 } }}>
                  <Typography><strong>Part Number:</strong> {part.internal_part_number}</Typography>
                  <Typography><strong>Description:</strong> {part.description}</Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Type:</strong> {part.part_type?.name || 'Unknown'}
                    <Chip
                      label={part.is_special_order ? 'Special Order' : 'Stock'}
                      color={part.is_special_order ? 'info' : 'default'}
                      size="small"
                    />
                  </Typography>
                  <Typography><strong>Returnable:</strong> {part.is_returnable ? 'Yes' : 'No'}</Typography>
                  <Typography><strong>Can Bundle:</strong> {part.can_bundle ? 'Yes' : 'No'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ '& > p': { mb: 2 } }}>
                  <Typography><strong>Cost:</strong> ${part.cost ? parseFloat(part.cost).toFixed(2) : 'N/A'}</Typography>
                  <Typography><strong>Reorder Point:</strong> {part.reorder_point || 'N/A'}</Typography>
                  <Typography><strong>Preferred Supplier:</strong> {part.preferred_supplier?.name || 'None'}</Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Total Quantity:</strong> {totalQuantity}
                    {part.reorder_point && totalQuantity <= part.reorder_point &&
                      <Chip label="Low Stock" color="error" size="small" />
                    }
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Inventory Locations Paper */}
          <Paper sx={{ mb: 4, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Inventory Locations</Typography>
            {inventory.length === 0 ? (
              <Alert severity="info">No inventory found for this part.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Warehouse</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventory.map(item => {
                      // Find warehouse-specific reorder point if it exists
                      const warehouseId = item.location?.warehouse?.id;
                      const warehouseReorder = part.warehouse_reorders?.find(
                        wr => wr.warehouse_id === warehouseId
                      );
                      const reorderPoint = warehouseReorder ? warehouseReorder.reorder_point : part.reorder_point;
                      const isLowStock = reorderPoint && item.quantity <= reorderPoint;

                      return (
                        <TableRow key={item.id}>
                          <TableCell>{item.location?.warehouse?.name || 'N/A'}</TableCell>
                          <TableCell>{item.location ? `${item.location.rack}-${item.location.shelf}` : 'N/A'}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            <Chip
                              label={isLowStock ? 'Low Stock' : 'In Stock'}
                              color={isLowStock ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Warehouse-Specific Reorder Points */}
          {!part.is_special_order && (
            <WarehouseReorderList
              partId={part.id}
              warehouseReorders={part.warehouse_reorders || []}
              onReorderChange={() => {
                // Refresh part data to get updated warehouse reorder points
                getPartById(id).then(data => setPart(data));
              }}
            />
          )}

          {/* Part Technician Requirements */}
          {!part.is_special_order && (
            <PartTechnicianRequirementList
              partId={part.id}
              requirement={technicianRequirement}
              onRequirementChange={(updatedRequirement) => {
                setTechnicianRequirement(updatedRequirement);
                // Refresh part data to get updated technician requirement
                getPartById(id).then(data => setPart(data));
              }}
            />
          )}

          {/* Transaction History Paper */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Transaction History</Typography>
            {transactions.length === 0 ? (
              <Alert severity="info">No transaction history found for this part.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Quantity Change</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map(transaction => (
                      <TableRow key={transaction.id}>
                        <TableCell>{new Date(transaction.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.transaction_type.charAt(0).toUpperCase() + transaction.transaction_type.slice(1)}
                            color={
                              transaction.transaction_type === 'receive' ? 'success' :
                              transaction.transaction_type === 'issue' ? 'error' :
                              transaction.transaction_type === 'adjust' ? 'warning' :
                              transaction.transaction_type === 'return' ? 'info' :
                              'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{transaction.location ? `${transaction.location.rack}-${transaction.location.shelf}` : 'N/A'}</TableCell>
                        <TableCell>{transaction.quantity_change > 0 ? `+${transaction.quantity_change}` : transaction.quantity_change}</TableCell>
                        <TableCell>{transaction.user.username}</TableCell>
                        <TableCell>{transaction.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      ) : (
        <Alert variant="warning">Part not found</Alert>
      )}

      {/* Inventory Adjustment Dialog */}
      <Dialog
        open={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Inventory</DialogTitle>
        <form onSubmit={handleAdjustInventory}>
          <DialogContent>
            {adjustmentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {adjustmentError}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Location</InputLabel>
              <Select
                name="location_id"
                value={adjustment.location_id}
                onChange={handleAdjustmentChange}
                required
                label="Location"
              >
                <MenuItem value="">
                  <em>Select Location</em>
                </MenuItem>
                {locations.map(location => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.warehouse?.name || 'Unknown Warehouse'} - {location.rack}-{location.shelf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                name="transaction_type"
                value={adjustment.transaction_type}
                onChange={handleAdjustmentChange}
                required
                label="Transaction Type"
              >
                <MenuItem value="receive">Receive (Add)</MenuItem>
                <MenuItem value="issue">Issue (Remove)</MenuItem>
                <MenuItem value="adjust">Adjust (Correction)</MenuItem>
                <MenuItem value="return">Return</MenuItem>
                <MenuItem value="transfer">Transfer</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Quantity Change"
              type="number"
              name="quantity_change"
              value={adjustment.quantity_change}
              onChange={handleAdjustmentChange}
              required
              placeholder="Enter quantity"
              helperText={
                adjustment.transaction_type === 'receive' || adjustment.transaction_type === 'return'
                  ? 'Enter a positive number to add to inventory'
                  : adjustment.transaction_type === 'issue'
                  ? 'Enter a negative number to remove from inventory'
                  : 'Enter a positive or negative number to adjust inventory'
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={adjustment.notes}
              onChange={handleAdjustmentChange}
              multiline
              rows={3}
              placeholder="Optional notes about this adjustment"
            />
          </DialogContent>
          <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 2, pb: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={() => setShowAdjustModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={adjustmentLoading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                disabled={adjustmentLoading}
              >
                {adjustmentLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Part Dialog */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Part</DialogTitle>
        <form onSubmit={handleUpdatePart}>
          <DialogContent>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {editError}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Part Number"
                    name="internal_part_number"
                    value={editedPart.internal_part_number || ''}
                    onChange={handleEditChange}
                    helperText="Unique internal identifier for this part"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={2}
                    label="Description"
                    name="description"
                    value={editedPart.description || ''}
                    onChange={handleEditChange}
                    helperText="Detailed description of the part"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Inventory Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cost"
                    name="cost"
                    value={editedPart.cost || ''}
                    onChange={handleEditChange}
                    inputProps={{ step: "0.01", min: "0" }}
                    helperText="Unit cost of the part"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Reorder Point"
                    name="reorder_point"
                    value={editedPart.reorder_point || ''}
                    onChange={handleEditChange}
                    inputProps={{ min: "0" }}
                    helperText="Minimum quantity before reordering"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Part Properties
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedPart.is_special_order || false}
                        onChange={handleEditChange}
                        name="is_special_order"
                      />
                    }
                    label="Special Order Item"
                  />
                  <Typography variant="caption" display="block" sx={{ ml: 4, mb: 2 }}>
                    Check if this part typically needs to be specially ordered
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedPart.is_returnable || false}
                        onChange={handleEditChange}
                        name="is_returnable"
                      />
                    }
                    label="Returnable"
                  />
                  <Typography variant="caption" display="block" sx={{ ml: 4, mb: 2 }}>
                    Check if this part can be returned to the supplier
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedPart.can_bundle || false}
                        onChange={handleEditChange}
                        name="can_bundle"
                      />
                    }
                    label="Can Bundle"
                  />
                  <Typography variant="caption" display="block" sx={{ ml: 4 }}>
                    Check if this part can be bundled with other parts
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 2, pb: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={editLoading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                disabled={editLoading}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to delete this part? This action cannot be undone.
          </Typography>
          <Typography>
            <strong>Note:</strong> Parts with existing inventory cannot be deleted. You must first remove all inventory for this part.
          </Typography>
        </DialogContent>
        <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 2, pb: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePart}
              variant="contained"
              color="error"
              startIcon={deleteLoading ? <CircularProgress size={24} color="inherit" /> : <DeleteIcon />}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Part'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PartDetails;
